import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import PhoneField from './PhoneField';

const MONTHS = ['January','February','March','April','May','June',
                 'July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(1);
  r.setMonth(r.getMonth() + n);
  return r;
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmt12(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')} ${period}`;
}

type Step = 'date' | 'time' | 'info' | 'confirmed';

interface BookingFlowProps {
  title: string;
  subtitle: string;
  /** API path for fetching slots, e.g. '/booking/slots' */
  slotsPath: string;
  /** API path for submitting the booking, e.g. '/booking' */
  bookPath: string;
  backLinkText: string;
  submitLabel: string;
  confirmedBackText: string;
  /** When true, an optional Company field is shown in the info form */
  showCompanyField?: boolean;
}

export default function BookingFlow({
  title, subtitle, slotsPath, bookPath,
  backLinkText, submitLabel, confirmedBackText,
  showCompanyField = false,
}: BookingFlowProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = addMonths(today, 2);

  const [step, setStep]               = useState<Step>('date');
  const [calMonth, setCalMonth]       = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [slots, setSlots]             = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm]               = useState({ name: '', email: '', phone: '', company: '' });
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [meetLink, setMeetLink]       = useState('');

  const firstDow = calMonth.getDay();
  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) =>
      new Date(calMonth.getFullYear(), calMonth.getMonth(), i + 1)
    ),
  ];

  const canPrevMonth = calMonth > new Date(today.getFullYear(), today.getMonth(), 1);
  const canNextMonth = addMonths(calMonth, 1) < maxDate;

  const isDisabled = (d: Date) =>
    d < today || d >= maxDate || d.getDay() === 0 || d.getDay() === 6;

  const fetchSlots = async (d: Date) => {
    setSlotsLoading(true);
    setSlots([]);
    try {
      const res = await fetch(`${config.apiBaseUrl}${slotsPath}?date=${toIsoDate(d)}`);
      const data = await res.json();
      setSlots(data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateSelect = (d: Date) => {
    if (isDisabled(d)) return;
    setSelectedDate(d);
    setSelectedTime('');
    fetchSlots(d);
    setStep('time');
  };

  const handleTimeSelect = (t: string) => {
    setSelectedTime(t);
    setStep('info');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    setError('');
    try {
      const body: Record<string, string> = {
        date: toIsoDate(selectedDate),
        time: selectedTime,
        name: form.name,
        email: form.email,
        phone: form.phone,
      };
      if (showCompanyField && form.company) body.company = form.company;

      const res = await fetch(`${config.apiBaseUrl}${bookPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      setMeetLink(data.meet_link || '');
      setStep('confirmed');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = selectedDate
    ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  return (
    <div className="min-h-screen bg-dark-canvas flex flex-col">
      <nav className="bg-dark-canvas border-b border-dark-border px-6 py-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-fuchsia-400 to-violet-500 rounded-lg flex items-center justify-center shadow-md">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">CoachSight</span>
          </Link>
          <Link to="/" className="text-sm text-dark-muted hover:text-white transition-colors">
            {backLinkText}
          </Link>
        </div>
      </nav>

      <div className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white">{title}</h1>
            <p className="text-dark-muted mt-2">{subtitle}</p>
          </div>

          <div className="bg-dark-card rounded-2xl shadow-2xl border border-dark-border overflow-hidden">

            {/* ── Step: Date ── */}
            {step === 'date' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => setCalMonth(m => addMonths(m, -1))}
                    disabled={!canPrevMonth}
                    className="p-1.5 rounded-lg text-dark-muted hover:text-white hover:bg-dark-hover disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <span className="text-white font-semibold">
                    {MONTHS[calMonth.getMonth()]} {calMonth.getFullYear()}
                  </span>
                  <button
                    onClick={() => setCalMonth(m => addMonths(m, 1))}
                    disabled={!canNextMonth}
                    className="p-1.5 rounded-lg text-dark-muted hover:text-white hover:bg-dark-hover disabled:opacity-30 transition-colors"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 mb-2">
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-xs font-medium text-dark-muted py-1">{d}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const disabled = isDisabled(d);
                    const isToday  = toIsoDate(d) === toIsoDate(today);
                    return (
                      <button
                        key={i}
                        onClick={() => handleDateSelect(d)}
                        disabled={disabled}
                        className={`
                          aspect-square rounded-lg text-sm font-medium transition-colors
                          ${disabled
                            ? 'text-dark-muted opacity-30 cursor-not-allowed'
                            : 'text-white hover:bg-primary-600 cursor-pointer'
                          }
                          ${isToday && !disabled ? 'ring-1 ring-primary-400' : ''}
                        `}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-dark-muted text-center mt-4">
                  Mon–Fri · 9 AM – 6 PM Eastern · Next 2 months
                </p>
              </div>
            )}

            {/* ── Step: Time ── */}
            {step === 'time' && (
              <div className="p-6">
                <button onClick={() => setStep('date')} className="flex items-center gap-1 text-sm text-dark-muted hover:text-white mb-4">
                  <ChevronLeftIcon className="w-4 h-4" /> {dateLabel}
                </button>
                <h2 className="text-white font-semibold mb-4">Pick a time</h2>
                {slotsLoading ? (
                  <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-400" />
                  </div>
                ) : slots.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-dark-muted mb-4">No available slots on this day.</p>
                    <button onClick={() => setStep('date')} className="text-primary-400 hover:underline text-sm">
                      Choose another date
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                    {slots.map(t => (
                      <button
                        key={t}
                        onClick={() => handleTimeSelect(t)}
                        className="py-2.5 rounded-lg border border-dark-border text-sm text-white hover:border-primary-500 hover:bg-primary-600/20 transition-colors"
                      >
                        {fmt12(t)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Step: Contact Info ── */}
            {step === 'info' && (
              <div className="p-6">
                <button onClick={() => setStep('time')} className="flex items-center gap-1 text-sm text-dark-muted hover:text-white mb-4">
                  <ChevronLeftIcon className="w-4 h-4" /> {dateLabel} · {fmt12(selectedTime)}
                </button>
                <h2 className="text-white font-semibold mb-4">Your details</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Full name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-dark-canvas border border-dark-border text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-dark-canvas border border-dark-border text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="jane@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Phone</label>
                    <PhoneField
                      value={form.phone}
                      onChange={val => setForm(f => ({ ...f, phone: val }))}
                    />
                  </div>
                  {showCompanyField && (
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Company <span className="text-dark-muted font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                        className="w-full px-3 py-2 bg-dark-canvas border border-dark-border text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Acme Coaching"
                      />
                    </div>
                  )}
                  {error && <p className="text-red-400 text-sm">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 transition-colors"
                  >
                    {submitting ? 'Booking…' : submitLabel}
                  </button>
                </form>
              </div>
            )}

            {/* ── Step: Confirmed ── */}
            {step === 'confirmed' && (
              <div className="p-8 text-center">
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">You're booked!</h2>
                <p className="text-dark-muted mb-1">{dateLabel}</p>
                <p className="text-white font-medium mb-6">{fmt12(selectedTime)} Eastern</p>
                <p className="text-sm text-dark-muted mb-6">
                  A calendar invite and meeting link have been sent to <span className="text-white">{form.email}</span>.
                </p>
                {meetLink && (
                  <a
                    href={meetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mb-6 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Join Session
                  </a>
                )}
                <div>
                  <Link to="/" className="text-sm text-dark-muted hover:text-white transition-colors">
                    {confirmedBackText}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-dark-canvas border-t border-dark-border py-3 text-center text-dark-muted text-sm flex-shrink-0">
        &copy; {new Date().getFullYear()} CoachSight. All rights reserved.
      </footer>
    </div>
  );
}
